// Input validation utilities
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhone = (phone: string): boolean => {
  // Indian phone number validation
  const phoneRegex = /^(\+91|91)?[6-9]\d{9}$/;
  return phoneRegex.test(phone.replace(/\s+/g, ''));
};

export const validateName = (name: string): boolean => {
  return name.trim().length >= 2 && /^[a-zA-Z\s]+$/.test(name);
};

export const sanitizeInput = (input: string): string => {
  return input
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .trim();
};

export const validateRequired = (value: string): boolean => {
  return value.trim().length > 0;
};

export const validateMinLength = (value: string, minLength: number): boolean => {
  return value.trim().length >= minLength;
};

export const validateMaxLength = (value: string, maxLength: number): boolean => {
  return value.trim().length <= maxLength;
};

// Form validation schema
export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: string) => boolean;
  message: string;
}

export interface ValidationSchema {
  [key: string]: ValidationRule[];
}

export interface ValidationErrors {
  [key: string]: string[];
}

export const validateForm = (
  data: Record<string, string>,
  schema: ValidationSchema
): ValidationErrors => {
  const errors: ValidationErrors = {};

  Object.keys(schema).forEach((field) => {
    const value = data[field] || '';
    const rules = schema[field];
    const fieldErrors: string[] = [];

    rules.forEach((rule) => {
      if (rule.required && !validateRequired(value)) {
        fieldErrors.push(rule.message);
        return;
      }

      if (rule.minLength && !validateMinLength(value, rule.minLength)) {
        fieldErrors.push(rule.message);
        return;
      }

      if (rule.maxLength && !validateMaxLength(value, rule.maxLength)) {
        fieldErrors.push(rule.message);
        return;
      }

      if (rule.pattern && !rule.pattern.test(value)) {
        fieldErrors.push(rule.message);
        return;
      }

      if (rule.custom && !rule.custom(value)) {
        fieldErrors.push(rule.message);
        return;
      }
    });

    if (fieldErrors.length > 0) {
      errors[field] = fieldErrors;
    }
  });

  return errors;
};

// Common validation schemas
export const contactFormSchema: ValidationSchema = {
  name: [
    { required: true, message: 'Name is required' },
    { minLength: 2, message: 'Name must be at least 2 characters' },
    { custom: validateName, message: 'Name can only contain letters and spaces' },
  ],
  email: [
    { required: true, message: 'Email is required' },
    { custom: validateEmail, message: 'Please enter a valid email address' },
  ],
  phone: [
    { required: true, message: 'Phone number is required' },
    { custom: validatePhone, message: 'Please enter a valid Indian phone number' },
  ],
  service: [
    { required: true, message: 'Please select a service' },
  ],
  message: [
    { maxLength: 1000, message: 'Message cannot exceed 1000 characters' },
  ],
};